// La puerta: lo que espera un si o un no (4.3, D8, R8).
//
// **Una sola puerta para todo lo que el sistema propone.** Memorias que
// dedujo, la referencia de un archivo recien subido, y las propuestas de
// organizacion que se quedaron sin sitio al quitar el buzon. Son tres tablas
// distintas y una sola pregunta: quien decide no tiene por que saber en que
// tabla vive lo que esta aprobando.
//
// **Va arriba de la lista, no en una pantalla aparte.** Un buzon separado hay
// que acordarse de abrirlo, y lo que nadie abre se queda esperando para
// siempre: eso ya paso aqui y por eso se quito.
//
// **Lo que se lee es el efecto, no el motivo.** El motivo explica por que se
// propone; el efecto dice que va a pasar si dices que si, que es lo que de
// verdad se esta aprobando. Aceptar leyendo solo el motivo es aceptar a
// ciegas.
//
// **El no pide motivo.** Sin el, el sistema vuelve a proponer lo mismo mañana
// y nadie recuerda que ya se dijo que no.
//
// **Y hay un tercer boton: cambiar** (4.6). Lo que el agente propone al leer un
// archivo esta escrito por el agente, y decir «si» a una linea mal redactada la
// deja leyendola mal en cada turno hasta que alguien se acuerde de arreglarla.
// Corregirla antes del si es mas barato y mas honesto. Solo lo llevan las
// memorias: una regla o una propuesta de organizacion no son una frase que el
// agente lea, asi que no hay texto que reescribir.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { workspacesService, type Pendiente } from '@/services/workspacesService'

/**
 * Lo que se lee de una propuesta: **que es**, no que pasara si dices que si.
 *
 * Tres fuentes por orden, porque las tres familias guardan la frase en un sitio
 * distinto. Una memoria trae su linea en `propone`; una propuesta de organizar
 * archivos manda ahi un objeto y lo que se lee es su `motivo`. El efecto es el
 * ultimo recurso: es el mismo texto para todas las de su tipo, asi que de
 * titular hace que cuatro propuestas distintas se lean identicas.
 */
function loQueEs(pendiente: Pendiente): string {
  if (typeof pendiente.propone === 'string' && pendiente.propone.trim()) {
    return pendiente.propone
  }
  return pendiente.motivo || pendiente.efecto || ''
}

export function PendingGate({
  pendientes,
  onResuelta,
}: {
  pendientes: Pendiente[]
  onResuelta: () => void
}) {
  const { t } = useTranslation()
  const [rechazando, setRechazando] = useState('')
  const [motivo, setMotivo] = useState('')
  const [corrigiendo, setCorrigiendo] = useState('')
  const [linea, setLinea] = useState('')
  const [trabajando, setTrabajando] = useState('')

  if (pendientes.length === 0) return null

  const resolver = async (
    pendiente: Pendiente,
    decision: 'aprobar' | 'rechazar',
    porQue = '',
  ) => {
    setTrabajando(pendiente.id)
    try {
      await workspacesService.resolverPendiente({
        que: pendiente.que,
        id: pendiente.id,
        decision,
        por: t('knowledge.puerta.firma'),
        motivo: porQue,
      })
      setRechazando('')
      setMotivo('')
      onResuelta()
    } finally {
      setTrabajando('')
    }
  }

  const corregir = async (pendiente: Pendiente) => {
    setTrabajando(pendiente.id)
    try {
      await workspacesService.corregirMemoria(pendiente.id, linea.trim())
      setCorrigiendo('')
      setLinea('')
      // No la aprueba: sigue esperando su si, ahora con el texto bueno.
      onResuelta()
    } finally {
      setTrabajando('')
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">
        {t('knowledge.puerta.titulo', { count: pendientes.length })}
      </h3>

      <ul className="space-y-4">
        {pendientes.map((pendiente) => (
          <li key={`${pendiente.que}-${pendiente.id}`} className="space-y-2">
            {/* **Delante que es; detras que pasara si dices que si.** Estuvo
                al reves hasta el 2026-09-21, con el efecto de titular, y el
                efecto es el mismo texto para todos los archivos: cuatro
                propuestas distintas se leian identicas y lo unico que las
                distinguia -que es cada una- estaba en gris y pequeño debajo.
                Lo dijo solo quien mejor conoce el producto: «no entiendo por
                que me dice algo tan generico».

                Y **solo si es una frase**: una propuesta de organizar archivos
                manda ahi un objeto, y de esas lo que se lee es el motivo. */}
            <p className="text-sm">{loQueEs(pendiente)}</p>
            <p className="text-xs text-muted-foreground">
              {[
                pendiente.seLlama,
                pendiente.archivo,
                // El efecto pasa aqui, detras de lo que es. Y el motivo solo si
                // no subio al titular, para no decirlo dos veces.
                pendiente.efecto,
                loQueEs(pendiente) === pendiente.motivo ? null : pendiente.motivo,
                pendiente.propuestaEn,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>

            {rechazando === pendiente.id ? (
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="max-w-xs"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder={t('knowledge.puerta.por_que_no')}
                  aria-label={t('knowledge.puerta.por_que_no')}
                />
                <Button
                  size="xs"
                  variant="destructive"
                  disabled={!motivo.trim() || trabajando === pendiente.id}
                  onClick={() => resolver(pendiente, 'rechazar', motivo.trim())}
                >
                  {t('knowledge.puerta.descartar')}
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setRechazando('')}>
                  {t('knowledge.puerta.volver')}
                </Button>
              </div>
            ) : corrigiendo === pendiente.id ? (
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="max-w-md"
                  value={linea}
                  onChange={(e) => setLinea(e.target.value)}
                  aria-label={t('knowledge.puerta.la_linea')}
                />
                <Button
                  size="xs"
                  disabled={!linea.trim() || trabajando === pendiente.id}
                  onClick={() => corregir(pendiente)}
                >
                  {t('knowledge.puerta.guardar_texto')}
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setCorrigiendo('')}>
                  {t('knowledge.puerta.volver')}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="xs"
                  disabled={trabajando === pendiente.id}
                  onClick={() => resolver(pendiente, 'aprobar')}
                >
                  {t('knowledge.puerta.si')}
                </Button>
                {/* Solo una memoria tiene una linea que reescribir. */}
                {pendiente.que === 'anotacion' && (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      setCorrigiendo(pendiente.id)
                      // Solo sale para `anotacion`, que siempre trae frase; aun
                      // asi pasa por aqui y no por el campo, que miente.
                      setLinea(loQueEs(pendiente))
                    }}
                  >
                    {t('knowledge.puerta.cambiar')}
                  </Button>
                )}
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    setRechazando(pendiente.id)
                    setMotivo('')
                  }}
                >
                  {t('knowledge.puerta.no')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
